import React, { useMemo, useId } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';

export interface Option {
  value: string;
  label: string;
}

export interface TariffSelectProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
  showErrors?: boolean;
  /** Optional name for form integrations */
  name?: string;
  /** Optional placeholder label shown as a disabled option */
  placeholder?: string;
  /** Disable the select */
  disabled?: boolean;
  /** Override the visible label if needed */
  label?: string;
}

/** Text constants for reuse / i18n hook-up. */
const TARIFF_SELECT_LABEL_DEFAULT = 'Select Tariff';

const TariffSelect: React.FC<TariffSelectProps> = ({
  options,
  value,
  onChange,
  error,
  showErrors = false,
  name,
  placeholder,
  disabled = false,
  label = TARIFF_SELECT_LABEL_DEFAULT,
}) => {
  const isError = Boolean(showErrors && error);
  const selectId = useId();
  const labelId = `${selectId}-label`;
  const helperTextId = `${selectId}-helper`;

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value as string);
  };

  const renderedOptions = useMemo(
    () =>
      options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      )),
    [options]
  );

  return (
    <FormControl
      fullWidth
      size="small"
      error={isError}
      disabled={disabled}
      aria-describedby={isError ? helperTextId : undefined}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        id={selectId}
        name={name}
        label={label}
        value={value}
        onChange={handleChange}
        aria-invalid={isError || undefined}
      >
        {placeholder && (
          <MenuItem value="" disabled>
            {placeholder}
          </MenuItem>
        )}
        {renderedOptions}
      </Select>
    </FormControl>
  );
};

export default TariffSelect;